/**
 * yt-approve-change.js — controller for sections/yt-approve-change.liquid
 * (partner programme Phase 6, docs/12-partners/03 §2.7–2.8, §3.4, §3.8;
 * wording docs/12-partners/legal/client-consent-v1.md §3,
 * notice version prt-change-notice-v1).
 *
 * A client opens the link in the email about a change their practitioner
 * suggested to a subscription. The link carries the request token in the
 * URL FRAGMENT (#t=<43-char base64url>&d=approve|decline). We stash it in
 * sessionStorage, strip it from the address bar, then talk to the
 * formulator through the App Proxy — POST only, token in the BODY:
 *
 *   POST /apps/yutrients/approvals/view {token}
 *        → { status, status_reason, can_decide, blocker, notice_version,
 *            partner:{display_name, profession_label}, client_first_name,
 *            stack_name, plan_period:'30d'|'90d', plan_label,
 *            price_before_gbp, price_after_gbp,
 *            before:{base, items:[{name, amount_mg, unit, from_base}]},
 *            after:{…}, changes:{added, removed, changed, base_change},
 *            next_charge_date, effective_charge_date, expires_at, note,
 *            safety_notice:{partner_name}|null, signed_in_mismatch,
 *            decided_at, applied_at, require_email_confirm }
 *        404 unknown token · 410 {error:'expired'} · a decided, applied,
 *        cancelled or expired request comes back with `status` set and
 *        can_decide:false (the base is a name string, `{slug,name}` or null)
 *   POST /apps/yutrients/approvals/decide
 *        {token, decision:'approve'|'decline', accepted_price_after_gbp?,
 *         reason?, email?}
 *        → {status:'approved', apply:'queued'|'deferred', effective_charge_date}
 *        | {status:'declined'}
 *        409 price_changed {details:{price_before_gbp, price_after_gbp,
 *            cadence_label, effective_charge_date}} (re-confirm) ·
 *        403 wrong_account (details.code 'email_mismatch' when the typed
 *            email is wrong) · 409 already_decided {details:{status}} ·
 *        410 expired · 409 subscription_changed · 422 validation_failed
 *   POST /apps/yutrients/approvals/status {token}
 *        → {status, status_reason, effective_charge_date, decided_at,
 *           applied_at, apply_after}  (polled every 3 s for up to 60 s)
 *
 * Every error is the flat {error, message, details?} envelope.
 *
 * The `d=` choice in the fragment only moves focus to the matching
 * button; nothing is ever submitted without a click, and approving needs
 * the unticked-by-default price checkbox. Hygiene: the token only ever
 * travels in the three fetch bodies above — never in the address bar, a
 * link, storage other than sessionStorage, or any tracking call. No
 * analytics fire from this file.
 */
(function () {
  'use strict';

  var PROXY = '/apps/yutrients';
  var TOKEN_KEY = 'yt_change_token';
  var HINT_KEY = 'yt_change_hint';
  var TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;
  var POLL_EVERY_MS = 3000;
  var POLL_FOR_MS = 60000;
  var REASON_MAX = 200;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // ── Formatting ──────────────────────────────────────────────────────
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  /** Control characters out of anything that lands in a heading. */
  function clean(s) {
    return String(s == null ? '' : s).replace(/[\u0000-\u001f\u007f]+/g, ' ').trim();
  }
  var TITLES = { dr: 1, mr: 1, mrs: 1, ms: 1, miss: 1, mx: 1, prof: 1, professor: 1, sir: 1, dame: 1 };
  /** "Sam Smith" → "Sam"; "Dr Jones" stays "Dr Jones" (same rule as the emails). */
  function firstName(name) {
    var c = clean(name);
    if (!c) return '';
    var first = c.split(/\s+/)[0] || c;
    return TITLES[first.toLowerCase().replace(/\.$/, '')] ? c : first;
  }
  function num(n) {
    var v = Number(n);
    return n == null || n === '' || !isFinite(v) ? null : v;
  }
  /** Prices as pounds and pence, so the echoed figure is exactly what the page showed. */
  function gbp(n) {
    var v = num(n);
    return v == null ? null : Math.round(v * 100) / 100;
  }
  function money(n) {
    var v = num(n);
    return v == null ? '—' : '£' + v.toFixed(2);
  }
  function trimNum(n) { return String(Math.round(n * 1000) / 1000); }
  /** Stored units → display, as the emails do: mg as-is, mcg ×1000 µg, bn_cfu "billion CFU". */
  function fmtAmount(mg, unit) {
    var n = Number(mg);
    if (!isFinite(n)) return '—';
    var u = String(unit || '').toLowerCase();
    if (u === 'mcg') return trimNum(n * 1000) + 'µg';
    if (u === 'bn_cfu') return trimNum(n) + ' billion CFU';
    return trimNum(n) + 'mg';
  }
  /** "Tuesday 22 September 2026" — the emails' form (some browsers add a comma after the weekday). */
  function fmtDay(d) {
    try {
      return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).replace(/^([A-Za-z]+),/, '$1');
    } catch (_e) { return d.toDateString(); }
  }
  /** 'YYYY-MM-DD' is a calendar day, not a moment: build it locally so it never shifts a day. */
  function fmtYmd(ymd) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
    if (!m) return 'your next charge date';
    return fmtDay(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  }
  function fmtIso(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    return isNaN(d.getTime()) ? '' : fmtDay(d);
  }
  function fmtIsoAt(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    var time = '';
    try { time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); } catch (_e) {}
    return fmtDay(d) + (time ? ' at ' + time : '');
  }
  function cadenceWords(period) {
    return period === '90d' ? 'every 3 months, delivered monthly' : 'every month';
  }
  function fromWords(period, ymd) {
    return period === '90d' ? 'the charge after ' + fmtYmd(ymd) : 'your charge on ' + fmtYmd(ymd);
  }
  function stayWords(period) {
    return period === '90d' ? 'Deliveries already paid for stay as they are.' : 'Tins already made or paid for stay as they are.';
  }
  function baseName(b) {
    if (!b) return null;
    if (typeof b === 'string') return clean(b) || null;
    if (typeof b === 'object') return clean(b.name || b.slug) || null;
    return null;
  }
  async function safeJson(res) {
    try { return await res.json(); } catch (_e) { return null; }
  }
  function safetyLine(name) {
    return 'Some amounts are above our standard daily maximum. ' + name + ' has set them for you as your practitioner. If you are pregnant, breastfeeding, taking medication or under medical care, speak to your GP or pharmacist first.';
  }

  // ── Token ───────────────────────────────────────────────────────────
  // The section also runs a tiny parse-time stash-and-strip so the
  // fragment is gone before most of the page's other scripts run; this is
  // the belt to that brace.
  function readToken() {
    var t = null;
    var h = location.hash || '';
    var m = /(?:^#|[#&])t=([A-Za-z0-9_-]+)(?:&|$)/.exec(h);
    if (m) {
      t = m[1];
      var d = /(?:^#|[#&])d=(approve|decline)(?:&|$)/.exec(h);
      try {
        sessionStorage.setItem(TOKEN_KEY, t);
        if (d) sessionStorage.setItem(HINT_KEY, d[1]); else sessionStorage.removeItem(HINT_KEY);
      } catch (_e) {}
      try { history.replaceState(null, '', location.pathname + location.search); } catch (_e) {}
    }
    if (!t) {
      try { t = sessionStorage.getItem(TOKEN_KEY); } catch (_e) {}
    }
    return t && TOKEN_RE.test(t) ? t : null;
  }
  /** The email button's choice — read once, then forgotten. */
  function takeHint() {
    var h = null;
    try { h = sessionStorage.getItem(HINT_KEY); sessionStorage.removeItem(HINT_KEY); } catch (_e) {}
    return h === 'approve' || h === 'decline' ? h : null;
  }

  function post(path, body) {
    return fetch(PROXY + path, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  // ── Panels ──────────────────────────────────────────────────────────
  var root, panels, cfg;

  function fillSlots(panel, slots) {
    if (!panel || !slots) return;
    panel.querySelectorAll('[data-yta-slot]').forEach(function (el) {
      var k = el.getAttribute('data-yta-slot');
      if (Object.prototype.hasOwnProperty.call(slots, k)) el.textContent = slots[k];
    });
  }
  function setState(name, slots) {
    if (!panels[name]) name = 'invalid';
    root.setAttribute('data-yt-state', name);
    Object.keys(panels).forEach(function (k) { panels[k].hidden = k !== name; });
    fillSlots(panels[name], slots);
    if (name !== 'loading' && name !== 'request') {
      var h = panels[name].querySelector('h1, h2');
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (_e) {} }
    }
  }
  function partnerSlots(model) {
    var partner = model.partner || 'Your practitioner';
    return {
      partner: partner,
      'partner-first': model.pFirst || 'them',
      date: fmtYmd(model.effective),
      'decided-on': model.decidedAt ? ' on ' + fmtIsoAt(model.decidedAt) : '',
    };
  }
  function cancelledPanel(reason) {
    return reason === 'subscription_changed' || reason === 'subscription_inactive' || reason === 'plan_changed'
      ? 'subscription-changed' : 'withdrawn';
  }
  /** A request that is no longer pending: which notice, and whether to keep polling. */
  function showStatus(status, reason, model) {
    var slots = partnerSlots(model);
    if (status === 'applied') return setState('applied', slots);
    if (status === 'approved' || status === 'applying') { setState('already-approved', slots); return pollStatus(model); }
    if (status === 'declined') return setState('already-declined', slots);
    if (status === 'failed') return setState('failed', slots);
    if (status === 'cancelled') return setState(cancelledPanel(reason), slots);
    if (status === 'expired') return setState('expired', slots);
    return setState('invalid', slots);
  }
  function showRetry(panel, fn) {
    var btn = panels[panel].querySelector('[data-yta-retry]');
    if (btn) { btn.onclick = fn; btn.disabled = false; }
    setState(panel);
  }

  // ── Load ────────────────────────────────────────────────────────────
  async function loadRequest(token, hint) {
    setState('loading');
    var res;
    try {
      res = await post('/approvals/view', { token: token });
    } catch (_e) {
      return showRetry('error', function () { loadRequest(token, hint); });
    }
    var v = await safeJson(res);
    if (res.status === 404) return setState('invalid');
    if (res.status === 410) return setState('expired');
    if (!res.ok || !v || typeof v !== 'object') return showRetry('error', function () { loadRequest(token, hint); });

    var model = modelFrom(v, token);
    if (v.status !== 'pending') return showStatus(v.status, v.status_reason, model);
    if (v.signed_in_mismatch) return setState('wrong-account', partnerSlots(model));
    if (!v.can_decide || model.priceAfter == null) {
      return showRetry('blocked', function () { loadRequest(token, hint); });
    }
    render(v, model, hint);
  }

  function modelFrom(v, token) {
    var partner = clean(v.partner && v.partner.display_name) || 'Your practitioner';
    return {
      token: token,
      partner: partner,
      pFirst: firstName(partner) || 'your practitioner',
      profession: clean(v.partner && v.partner.profession_label),
      client: firstName(v.client_first_name),
      stack: clean(v.stack_name),
      period: v.plan_period === '90d' ? '90d' : '30d',
      priceBefore: gbp(v.price_before_gbp),
      priceAfter: gbp(v.price_after_gbp),
      effective: v.effective_charge_date || v.next_charge_date || null,
      expiresAt: v.expires_at,
      decidedAt: v.decided_at,
      requireEmail: !!v.require_email_confirm,
      busy: false,
    };
  }

  // ── Render ──────────────────────────────────────────────────────────
  function rowsHtml(before, after) {
    var key = function (i) { return clean(i && i.name).toLowerCase(); };
    var bAdd = (before || []).filter(function (i) { return i && !i.from_base; });
    var aAdd = (after || []).filter(function (i) { return i && !i.from_base; });
    var aMap = {};
    aAdd.forEach(function (i) { aMap[key(i)] = i; });
    var bMap = {};
    bAdd.forEach(function (i) { bMap[key(i)] = i; });
    var rows = [];
    bAdd.forEach(function (b) {
      var a = aMap[key(b)];
      if (!a) rows.push({ name: b.name, before: fmtAmount(b.amount_mg, b.unit), after: null, mark: 'removed' });
      else if (Math.abs(Number(a.amount_mg) - Number(b.amount_mg)) > 1e-6) rows.push({ name: a.name, before: fmtAmount(b.amount_mg, b.unit), after: fmtAmount(a.amount_mg, a.unit), mark: 'changed' });
      else rows.push({ name: a.name, before: fmtAmount(b.amount_mg, b.unit), after: fmtAmount(a.amount_mg, a.unit), mark: '' });
    });
    aAdd.forEach(function (a) {
      if (!bMap[key(a)]) rows.push({ name: a.name, before: null, after: fmtAmount(a.amount_mg, a.unit), mark: 'added' });
    });
    if (!rows.length) return '';
    var dash = '<span class="yta-dash" aria-label="none">—</span>';
    var tags = { added: 'Added', removed: 'Removed', changed: 'Changed' };
    return rows.map(function (r) {
      return (
        '<tr class="' + (r.mark ? 'yta-tr--' + r.mark : '') + '">' +
          '<td><span class="yta-t__n"><span>' + esc(clean(r.name)) + '</span>' +
            (r.mark ? '<span class="yta-tag yta-tag--' + r.mark + '">' + tags[r.mark] + '</span>' : '') +
          '</span></td>' +
          '<td>' + (r.before == null ? dash : esc(r.before)) + '</td>' +
          '<td>' + (r.after == null ? dash : esc(r.after)) + '</td>' +
        '</tr>'
      );
    }).join('');
  }

  function baseHtml(v) {
    var from = baseName(v.before && v.before.base);
    var to = baseName(v.after && v.after.base);
    var changed = !!(v.changes && v.changes.base_change) || from !== to;
    if (!from && !to) return '';
    var items = ((v.after && v.after.items) || []).filter(function (i) { return i && i.from_base; });
    var line;
    if (!changed) {
      line = '<b>' + esc(to) + '</b><span class="yta-muted">unchanged</span>';
    } else {
      var mark = !from ? 'added' : !to ? 'removed' : 'changed';
      line = '<span>' + esc(from || 'No base') + '</span><span class="yta-arrow" aria-hidden="true">→</span><b>' + esc(to || 'No base') + '</b>' +
        '<span class="yta-tag yta-tag--' + mark + '">' + (mark === 'added' ? 'Added' : mark === 'removed' ? 'Removed' : 'Changed') + '</span>';
    }
    return (
      '<div class="yta-base">' +
        '<span class="yta-base__tag" aria-hidden="true">Base</span>' +
        '<div class="yta-base__b">' +
          '<p class="yta-eyebrow">Base blend</p>' +
          '<p class="yta-base__n">' + line + '</p>' +
          (changed && to && items.length
            ? '<p class="yta-base__i">Includes ' + esc(items.map(function (i) { return clean(i.name) + ' ' + fmtAmount(i.amount_mg, i.unit); }).join(', ')) + '</p>'
            : '') +
        '</div>' +
      '</div>'
    );
  }

  function agreeText(model) {
    return 'I agree to pay <b>' + esc(money(model.priceAfter)) + ' ' + esc(cadenceWords(model.period)) + '</b> for this subscription from ' +
      (model.period === '90d' ? 'the charge after ' : 'my charge on ') + '<b>' + esc(fmtYmd(model.effective)) + '</b>' +
      (model.priceBefore != null ? ', instead of ' + esc(money(model.priceBefore)) : '') + '.';
  }

  function render(v, model, hint) {
    var expires = fmtIso(model.expiresAt);
    var stackWords = model.stack ? 'your <b>' + esc(model.stack) + '</b> subscription' : 'your subscription';
    var note = clean(v.note);
    var safety = v.safety_notice ? clean(v.safety_notice.partner_name) || model.partner : null;
    var hasBase = !!(baseName(v.before && v.before.base) || baseName(v.after && v.after.base));

    var head =
      '<header class="yta-head">' +
        '<div class="yta-prac">' +
          '<span class="yta-prac__img" aria-hidden="true">' + esc((model.pFirst || 'Y').charAt(0).toUpperCase()) + '</span>' +
          '<span class="yta-prac__b"><span class="yta-eyebrow">Suggested by</span><span class="yta-prac__n">' + esc(model.partner) + '</span>' +
            (model.profession ? '<span class="yta-prac__r">' + esc(model.profession) + '</span>' : '') +
          '</span>' +
        '</div>' +
        '<h1 class="yta-h1">' + (model.client ? 'Hi ' + esc(model.client) + ', ' : '') + esc(model.pFirst) + ' has suggested a change to ' + stackWords + '.</h1>' +
        '<p class="yta-p yta-lead"><b>Nothing changes unless you approve.</b> Have a look at what ' + esc(model.pFirst) + ' is suggesting' +
          (expires ? ' and decide by ' + esc(expires) : '') + '.</p>' +
        (note ? '<blockquote class="yta-note"><p data-yta-note></p><cite>Note from ' + esc(model.pFirst) + '</cite></blockquote>' : '') +
        (safety ? '<p class="yta-safety">' + esc(safetyLine(safety)) + '</p>' : '') +
      '</header>';

    var blend =
      '<section class="yta-card" aria-labelledby="yta-blend-h">' +
        '<div class="yta-card__head"><p class="yta-eyebrow">Your blend</p>' +
          '<h2 class="yta-h2" id="yta-blend-h">' + (model.stack ? esc(model.stack) : 'Your blend') + ' — now, and if you approve</h2></div>' +
        baseHtml(v) +
        '<div class="yta-scroll"><table class="yta-t">' +
          '<thead><tr><th scope="col">Ingredient</th><th scope="col">Now</th><th scope="col">If you approve</th></tr></thead>' +
          '<tbody>' + rowsHtml(v.before && v.before.items, v.after && v.after.items) + '</tbody>' +
        '</table></div>' +
        '<p class="yta-small">Daily amounts' + (hasBase ? '; the base’s own ingredients are included in the base' : '') + '. Everything not marked stays exactly as it is.</p>' +
      '</section>';

    var price =
      '<section class="yta-card" aria-labelledby="yta-price-h">' +
        '<div class="yta-card__head"><p class="yta-eyebrow">Price</p><h2 class="yta-h2" id="yta-price-h">What you’d pay</h2></div>' +
        '<div class="yta-banner" data-yta-price-banner role="alert" hidden></div>' +
        '<dl class="yta-price">' +
          '<div class="yta-price__row"><dt>Price now</dt><dd><span data-yta-price-before>' + esc(money(model.priceBefore)) + '</span> ' + esc(cadenceWords(model.period)) + '</dd></div>' +
          '<div class="yta-price__row yta-price__row--after"><dt>Price if you approve</dt><dd><b data-yta-price-after>' + esc(money(model.priceAfter)) + '</b> ' + esc(cadenceWords(model.period)) + '</dd></div>' +
          '<div class="yta-price__row yta-price__row--from"><dt>From</dt><dd data-yta-from>' + esc(fromWords(model.period, model.effective)) + '</dd></div>' +
        '</dl>' +
        '<p class="yta-small">' + esc(stayWords(model.period)) + ' Only the lines that change use today’s prices; everything you keep stays at the price you already pay. Delivery stays free.</p>' +
      '</section>';

    var emailField = model.requireEmail
      ? '<div class="yta-field" data-yta-email-field>' +
          '<label for="yta-email">The email address this suggestion was sent to</label>' +
          '<input id="yta-email" type="email" inputmode="email" autocomplete="email" spellcheck="false" data-yta-email aria-describedby="yta-email-err">' +
          '<p class="yta-err" id="yta-email-err" data-yta-email-err role="alert" hidden></p>' +
        '</div>'
      : '';

    var decide =
      '<div class="yta-decide" data-yta-decide>' +
        '<div class="yta-approve" data-yta-approve-block>' +
          '<p class="yta-eyebrow">To approve</p>' +
          '<label class="yta-check">' +
            '<input type="checkbox" data-yta-agree aria-describedby="yta-agree-err">' +
            '<span class="yta-check__box" aria-hidden="true"></span>' +
            '<span class="yta-check__t" data-yta-agree-text>' + agreeText(model) + '</span>' +
          '</label>' +
          '<p class="yta-err" id="yta-agree-err" data-yta-agree-err role="alert" hidden>Please tick the box to approve this change.</p>' +
          emailField +
          '<div class="yta-error" data-yta-error role="alert" hidden></div>' +
          '<button type="button" class="yta-btn" data-yta-approve>Approve this change</button>' +
        '</div>' +
        '<p class="yta-or"><span>or</span></p>' +
        '<div class="yta-decline" data-yta-decline-block>' +
          '<button type="button" class="yta-btn yta-btn--ghost" data-yta-decline-open aria-expanded="false" aria-controls="yta-decline-form">Decline</button>' +
          '<form id="yta-decline-form" data-yta-decline-form novalidate hidden>' +
            '<fieldset class="yta-radios"><legend>Why? (optional — ' + esc(model.pFirst) + ' will see your answer)</legend>' +
              radioHtml('not_now', 'Not now') +
              radioHtml('talk', 'I’d rather talk to my practitioner first') +
              radioHtml('other', 'Something else') +
            '</fieldset>' +
            '<div class="yta-field" data-yta-other hidden>' +
              '<label for="yta-reason">Tell us more (optional)</label>' +
              '<input id="yta-reason" type="text" maxlength="' + REASON_MAX + '" autocomplete="off" data-yta-reason>' +
              '<span class="yta-field__hint">Up to ' + REASON_MAX + ' characters.</span>' +
            '</div>' +
            '<div class="yta-error" data-yta-decline-error role="alert" hidden></div>' +
            '<div class="yta-actions">' +
              '<button type="submit" class="yta-btn" data-yta-decline>Decline this change</button>' +
              '<button type="button" class="yta-btn yta-btn--ghost yta-btn--small" data-yta-decline-back>Back</button>' +
            '</div>' +
          '</form>' +
          '<p class="yta-small">Didn’t expect this? Declining keeps your subscription exactly as it is. You can also <a href="' + esc(cfg.helpUrl) + '">contact us</a>.</p>' +
        '</div>' +
      '</div>';

    var foot =
      '<footer class="yta-foot">' +
        (expires ? '<p class="yta-small">This suggestion expires on ' + esc(expires) + '.</p>' : '') +
        '<p class="yta-small">You are sharing this subscription with ' + esc(model.partner) + ', which means they can see its blend, status and price, but never your address or payment details. ' +
          '<a href="' + esc(cfg.sharingUrl) + '">Stop sharing</a> · <a href="' + esc(cfg.privacyUrl) + '">Privacy Policy</a></p>' +
      '</footer>';

    panels.request.innerHTML = '<div class="yta-req">' + head + blend + price + decide + foot + '</div>';

    // The note is plain text — set via textContent so nothing in it renders.
    var noteEl = panels.request.querySelector('[data-yta-note]');
    if (noteEl) noteEl.textContent = '“' + note + '”';

    bind(model);
    setState('request');

    // The email button's choice: focus only, never submit.
    if (hint === 'approve') focusEl(panels.request.querySelector('[data-yta-approve]'));
    else if (hint === 'decline') focusEl(panels.request.querySelector('[data-yta-decline-open]'));
    else focusEl(panels.request.querySelector('h1'), true);
  }

  function radioHtml(value, label) {
    return (
      '<label class="yta-radio">' +
        '<input type="radio" name="yta-why" value="' + esc(value) + '" data-yta-why>' +
        '<span class="yta-radio__dot" aria-hidden="true"></span>' +
        '<span>' + esc(label) + '</span>' +
      '</label>'
    );
  }
  function focusEl(el, preventScroll) {
    if (!el) return;
    if (el.tagName === 'H1' || el.tagName === 'H2') el.setAttribute('tabindex', '-1');
    try { el.focus({ preventScroll: !!preventScroll }); } catch (_e) {}
    if (!preventScroll && el.scrollIntoView) {
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_e) {}
    }
  }

  // ── Interaction ─────────────────────────────────────────────────────
  function q(sel) { return panels.request.querySelector(sel); }
  function showError(sel, msg, retryFn) {
    var el = q(sel);
    if (!el) return;
    el.textContent = '';
    el.hidden = !msg;
    if (!msg) return;
    var p = document.createElement('p');
    p.textContent = msg;
    el.appendChild(p);
    if (retryFn) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'yta-btn yta-btn--ghost yta-btn--small';
      b.textContent = 'Try again';
      b.addEventListener('click', retryFn);
      el.appendChild(b);
    }
  }
  function agreeError(show) {
    var err = q('[data-yta-agree-err]');
    var block = q('[data-yta-approve-block]');
    if (err) err.hidden = !show;
    if (block) block.classList.toggle('is-invalid', !!show);
    if (show) focusEl(q('[data-yta-agree]'));
  }
  function emailError(msg) {
    var err = q('[data-yta-email-err]');
    var field = q('[data-yta-email-field]');
    if (err) { err.textContent = msg || ''; err.hidden = !msg; }
    if (field) field.classList.toggle('is-invalid', !!msg);
    if (msg) focusEl(q('[data-yta-email]'));
  }
  function setBusy(model, on, which) {
    model.busy = on;
    var approveBtn = q('[data-yta-approve]');
    var declineBtn = q('[data-yta-decline]');
    var openBtn = q('[data-yta-decline-open]');
    if (approveBtn) { approveBtn.disabled = on; approveBtn.setAttribute('aria-busy', on && which === 'approve' ? 'true' : 'false'); approveBtn.textContent = on && which === 'approve' ? 'Approving…' : 'Approve this change'; }
    if (declineBtn) { declineBtn.disabled = on; declineBtn.setAttribute('aria-busy', on && which === 'decline' ? 'true' : 'false'); declineBtn.textContent = on && which === 'decline' ? 'Declining…' : 'Decline this change'; }
    if (openBtn) openBtn.disabled = on;
    panels.request.querySelectorAll('[data-yta-agree], [data-yta-email], [data-yta-why], [data-yta-reason], [data-yta-decline-back]').forEach(function (i) { i.disabled = on; });
  }

  function bind(model) {
    var agree = q('[data-yta-agree]');
    if (agree) agree.addEventListener('change', function () { if (agree.checked) agreeError(false); });
    var email = q('[data-yta-email]');
    if (email) email.addEventListener('input', function () { emailError(''); });
    var approveBtn = q('[data-yta-approve]');
    if (approveBtn) approveBtn.addEventListener('click', function () { approve(model); });

    var open = q('[data-yta-decline-open]');
    var form = q('[data-yta-decline-form]');
    var back = q('[data-yta-decline-back]');
    if (open && form) {
      open.addEventListener('click', function () {
        form.hidden = false;
        open.hidden = true;
        open.setAttribute('aria-expanded', 'true');
        focusEl(form.querySelector('[data-yta-why]'));
      });
    }
    if (back && form && open) {
      back.addEventListener('click', function () {
        form.hidden = true;
        open.hidden = false;
        open.setAttribute('aria-expanded', 'false');
        showError('[data-yta-decline-error]', '');
        focusEl(open);
      });
    }
    panels.request.querySelectorAll('[data-yta-why]').forEach(function (r) {
      r.addEventListener('change', function () {
        panels.request.querySelectorAll('.yta-radio').forEach(function (l) { l.classList.toggle('is-on', l.contains(r) && r.checked); });
        var other = q('[data-yta-other]');
        if (other) {
          other.hidden = r.value !== 'other';
          if (r.value === 'other') focusEl(q('[data-yta-reason]'));
        }
      });
    });
    if (form) form.addEventListener('submit', function (e) { e.preventDefault(); decline(model); });
  }

  // ── Approve ─────────────────────────────────────────────────────────
  async function approve(model) {
    if (model.busy) return;
    showError('[data-yta-error]', '');
    var agree = q('[data-yta-agree]');
    if (!(agree && agree.checked)) return agreeError(true);
    agreeError(false);
    var email = null;
    if (model.requireEmail) {
      var input = q('[data-yta-email]');
      email = input ? input.value.trim() : '';
      if (!email || !EMAIL_RE.test(email)) return emailError('Enter the email address this suggestion was sent to.');
      emailError('');
    }
    setBusy(model, true, 'approve');
    var body = { token: model.token, decision: 'approve', accepted_price_after_gbp: model.priceAfter };
    if (email) body.email = email;
    var res, data;
    try {
      res = await post('/approvals/decide', body);
    } catch (_e) {
      setBusy(model, false);
      return showError('[data-yta-error]', 'We couldn’t reach Yutrients. Check your connection and try again — nothing has been approved yet.', function () { approve(model); });
    }
    data = await safeJson(res);
    if (res.ok && data && data.status === 'approved') {
      if (data.effective_charge_date) model.effective = data.effective_charge_date;
      if (data.apply === 'deferred') return setState('deferred', partnerSlots(model));
      setState('applying', partnerSlots(model));
      return pollStatus(model);
    }
    setBusy(model, false);
    decideError(res, data, model, 'approve');
  }

  // ── Decline ─────────────────────────────────────────────────────────
  function declineReason() {
    var chosen = q('[data-yta-why]:checked');
    var typed = q('[data-yta-reason]');
    var text = typed && !q('[data-yta-other]').hidden ? clean(typed.value) : '';
    if (!chosen) return text.slice(0, REASON_MAX) || null;
    if (chosen.value === 'other') return (text || 'Something else').slice(0, REASON_MAX);
    var label = chosen.parentNode && chosen.parentNode.querySelector('span:last-child');
    return (label ? clean(label.textContent) : chosen.value).slice(0, REASON_MAX);
  }
  async function decline(model) {
    if (model.busy) return;
    showError('[data-yta-decline-error]', '');
    setBusy(model, true, 'decline');
    var body = { token: model.token, decision: 'decline' };
    var reason = declineReason();
    if (reason) body.reason = reason;
    var res, data;
    try {
      res = await post('/approvals/decide', body);
    } catch (_e) {
      setBusy(model, false);
      return showError('[data-yta-decline-error]', 'We couldn’t reach Yutrients. Check your connection and try again — nothing has changed.', function () { decline(model); });
    }
    data = await safeJson(res);
    if (res.ok && data && data.status === 'declined') return setState('declined', partnerSlots(model));
    setBusy(model, false);
    decideError(res, data, model, 'decline');
  }

  // ── Decide errors (both paths) ──────────────────────────────────────
  function decideError(res, data, model, which) {
    var box = which === 'decline' ? '[data-yta-decline-error]' : '[data-yta-error]';
    var retry = which === 'decline' ? function () { decline(model); } : function () { approve(model); };
    var code = data && data.error;
    var det = (data && data.details) || {};
    var msg = data && typeof data.message === 'string' ? data.message : '';

    if (code === 'price_changed') return priceChanged(model, det);
    if (code === 'wrong_account' || res.status === 403) {
      if (det.code === 'email_mismatch' && model.requireEmail) return emailError(msg || 'That isn’t the email address this suggestion was sent to.');
      return setState('wrong-account', partnerSlots(model));
    }
    if (code === 'already_decided') return showStatus(det.status || 'applied', det.status_reason, model);
    if (code === 'expired' || res.status === 410) return setState('expired', partnerSlots(model));
    if (code === 'subscription_changed') return setState('subscription-changed', partnerSlots(model));
    if (code === 'not_found' || res.status === 404) return setState('invalid');
    if (code === 'validation_failed') {
      if (det.field === 'accepted_price_after_gbp') return showError(box, 'Something went wrong with the price on this page. Reload the page and try again — nothing has been approved.');
      if (model.requireEmail && /email/i.test(JSON.stringify(det))) return emailError('Enter the email address this suggestion was sent to.');
      return showError(box, 'Something went wrong. Reload the page and try again — nothing has changed.');
    }
    if (code === 'partner_changes_disabled') return showError(box, 'Suggested changes aren’t available right now. Please try again later — nothing has changed.');
    if (res.status === 429) return showError(box, 'Too many attempts just now. Wait a minute and try again.');
    if (code === 'recharge_unavailable' || code === 'conflict') return showError(box, msg || 'We couldn’t check your subscription just now. Please try again.', retry);
    showError(box, 'Something went wrong. Please try again — nothing has changed.', retry);
  }

  /** 409 price_changed: show the new figures, untick the box, ask again. */
  function priceChanged(model, det) {
    var oldAfter = model.priceAfter;
    if (gbp(det.price_after_gbp) != null) model.priceAfter = gbp(det.price_after_gbp);
    if (gbp(det.price_before_gbp) != null) model.priceBefore = gbp(det.price_before_gbp);
    if (det.effective_charge_date) model.effective = det.effective_charge_date;
    var before = q('[data-yta-price-before]');
    var after = q('[data-yta-price-after]');
    var from = q('[data-yta-from]');
    var text = q('[data-yta-agree-text]');
    var agree = q('[data-yta-agree]');
    if (before) before.textContent = money(model.priceBefore);
    if (after) after.textContent = money(model.priceAfter);
    if (from) from.textContent = fromWords(model.period, model.effective);
    if (text) text.innerHTML = agreeText(model);
    if (agree) agree.checked = false;
    var banner = q('[data-yta-price-banner]');
    if (banner) {
      banner.textContent = '';
      var b = document.createElement('b');
      b.textContent = 'The price has changed since this page was loaded.';
      var p = document.createElement('span');
      p.textContent = ' It is now ' + money(model.priceAfter) + ' ' + cadenceWords(model.period) +
        (oldAfter != null && oldAfter !== model.priceAfter ? ' (it was ' + money(oldAfter) + ')' : '') +
        '. Nothing has been approved — check the new figure and, if you’re happy with it, tick the box and approve again.';
      banner.appendChild(b);
      banner.appendChild(p);
      banner.hidden = false;
      focusEl(banner);
      banner.setAttribute('tabindex', '-1');
    }
    var box = q('[data-yta-approve-block]');
    if (box) box.classList.remove('is-invalid');
  }

  // ── Status polling ──────────────────────────────────────────────────
  var pollTimer = null;
  function pollStatus(model) {
    if (pollTimer) clearTimeout(pollTimer);
    var started = Date.now();
    function slow() {
      var panel = panels.applying;
      panel.querySelectorAll('[data-yta-slow]').forEach(function (el) { el.hidden = false; });
      var p = panel.querySelector('.yta-p--spinner');
      if (p) p.hidden = true;
    }
    async function tick() {
      pollTimer = null;
      var res = null, data = null;
      try {
        res = await post('/approvals/status', { token: model.token });
        data = await safeJson(res);
      } catch (_e) { res = null; }
      if (res && res.ok && data && typeof data === 'object') {
        var st = data.status;
        if (st === 'applied') {
          if (data.effective_charge_date) model.effective = data.effective_charge_date;
          return setState('applied', partnerSlots(model));
        }
        if (st === 'failed') return setState('failed', partnerSlots(model));
        if (st === 'cancelled') return setState(cancelledPanel(data.status_reason), partnerSlots(model));
        if (st === 'approved' && data.apply_after) {
          if (data.effective_charge_date) model.effective = data.effective_charge_date;
          return setState('deferred', partnerSlots(model));
        }
        if (st === 'declined') { model.decidedAt = data.decided_at || model.decidedAt; return setState('already-declined', partnerSlots(model)); }
        if (st === 'expired') return setState('expired', partnerSlots(model));
      } else if (res && res.status === 404) {
        return slow();
      }
      if (Date.now() - started >= POLL_FOR_MS) return slow();
      pollTimer = setTimeout(tick, POLL_EVERY_MS);
    }
    pollTimer = setTimeout(tick, POLL_EVERY_MS);
  }

  // ── Init ────────────────────────────────────────────────────────────
  function boot() {
    root = document.querySelector('[data-yt-approve-change]');
    if (!root) return;
    panels = {};
    root.querySelectorAll('[data-yta-state]').forEach(function (el) { panels[el.getAttribute('data-yta-state')] = el; });
    cfg = {
      helpUrl: root.getAttribute('data-yta-help-url') || '/pages/contact',
      privacyUrl: root.getAttribute('data-yta-privacy-url') || '/pages/privacy-policy',
      sharingUrl: root.getAttribute('data-yta-sharing-url') || '/pages/manage-subscription',
    };
    var token = readToken();
    var hint = takeHint();
    if (!token) return setState('invalid');
    loadRequest(token, hint);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
